// Click tracking for every page, in one handler so the order is fixed:
// 1. Campaign parameters: with granted consent, app links carry the current/last campaign
//    and the Rewardful referral id.
//    Meta visitors retain their ad parameters and the App Store button gets the /ios
//    campaign link (utils/tracking/adLinks.ts). Rewritten on pointerdown as well as click, so a
//    middle-click, a long-press "open in new tab" and TrialSignupDialog (which reads the clicked
//    link's href) all get the same URL.
// 2. Analytics and Meta click events (StartFreeClick, DemoRequestClick, PricingClick,
//    AppStoreRedirect).
// 3. If a Meta event fired and the click leaves the page in this tab, navigation waits 300 ms so
//    the Pixel request is not cancelled — the same pause /start and /ios use.
import { appStoreSearchForAd, isAppLink, isAppStoreListing, campaignParams, metaAdParams, withAdParams } from '~/utils/tracking/adLinks';
import { ATTRIBUTION_COOKIE, decodeAttribution } from '~/utils/tracking/attributionCookie';
import { readCookie } from '~/utils/tracking/consentRecord';
import { META_CLICK_EVENTS } from '~/utils/tracking/metaEvents';
import { CTA_TEST_ID, isCtaVariant } from '~/utils/tracking/ctaTest';
import { appStoreUrl } from '~/utils/tracking/redirectDestination';
import { isEmbedPath } from '~/data/tool-embeds.mjs';

type ConversionElement = HTMLElement & {
  dataset: DOMStringMap & {
    conversionEvent?: string;
    conversionLabel?: string;
    conversionLocation?: string;
    conversionPlan?: string;
    conversionVariant?: string;
  };
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const BEACON_FLUSH_MS = 300;

export default defineNuxtPlugin(() => {
  // Embedded calculators carry no app links or conversion clicks.
  if (import.meta.client && isEmbedPath(window.location.pathname)) return;
  const { trackCustom } = useMetaPixel();
  const { adConsent } = useCookieConsent();

  const anchorOf = (event: Event) => {
    const target = event.target instanceof Element ? event.target : null;
    return target?.closest<HTMLAnchorElement>("a[href]") ?? null;
  };

  // This visitor's Meta ad parameters: the current URL first, else w369_attr's last_meta.
  const visitorAdParams = () =>
    metaAdParams(
      window.location.search,
      decodeAttribution(readCookie(document.cookie, ATTRIBUTION_COOKIE))?.last_meta,
      adConsent.value === "granted",
    );

  // Rewardful sets its referral id asynchronously (and only after marketing consent), so links
  // rendered before it loaded lack ?ref=. Add it at click time; an authored ref wins.
  const withReferral = (href: string) => {
    const referral = window.Rewardful?.referral;
    if (!referral) return href;
    const url = new URL(href);
    if (url.searchParams.has("ref")) return href;
    url.searchParams.set("ref", referral);
    return url.toString();
  };

  // Restore our own decoration before recomputing: consent can be revoked or the
  // campaign can change between pointerdown and click. Preserve authored params.
  const decorated = new WeakMap<HTMLAnchorElement, { original: string; rendered: string }>();
  const decorate = (anchor: HTMLAnchorElement) => {
    const prior = decorated.get(anchor);
    if (prior && anchor.href === prior.rendered) anchor.href = prior.original;
    const original = anchor.href;
    let next = original;
    const state = decodeAttribution(readCookie(document.cookie, ATTRIBUTION_COOKIE));
    const params = campaignParams(window.location.search, state?.last, state?.last_meta, adConsent.value === 'granted');
    const metaParams = visitorAdParams();
    if (isAppLink(original)) next = withReferral(withAdParams(original, params));
    // Apple's existing campaign format and Meta event stay Meta-only.
    else if (isAppStoreListing(original) && metaParams) next = appStoreUrl(appStoreSearchForAd(metaParams));
    if (next !== original) anchor.href = next;
    decorated.set(anchor, { original, rendered: anchor.href });
    return metaParams;
  };

  // A plain left click that would replace this page with another site.
  const leavesPage = (event: MouseEvent, anchor: HTMLAnchorElement) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (anchor.hasAttribute("download")) return false;
    const target = anchor.target.toLowerCase();
    if (target && target !== "_self" && target !== "_top" && target !== "_parent") return false;
    try {
      const url = new URL(anchor.href);
      return /^https?:$/.test(url.protocol) && url.origin !== window.location.origin;
    } catch {
      return false;
    }
  };

  // Runs last for this click (window, bubble phase): if nothing else took the click over —
  // TrialSignupDialog prevents the default to open its sign-up sheet — leave 300 ms later.
  const holdNavigation = (event: MouseEvent, href: string) => {
    const finish = (seen: Event) => {
      window.removeEventListener("click", finish);
      if (seen !== event || seen.defaultPrevented) return;
      seen.preventDefault();
      window.setTimeout(() => window.location.assign(href), BEACON_FLUSH_MS);
    };
    window.addEventListener("click", finish);
  };

  const handlePointerDown = (event: PointerEvent) => {
    const anchor = anchorOf(event);
    if (anchor) decorate(anchor);
  };

  const handleClick = (event: MouseEvent) => {
    const anchor = anchorOf(event);
    const adParams = anchor ? decorate(anchor) : null;
    let pixelFired = false;

    // The App Store button, for a visitor from a Meta ad: the event /ios already sends.
    if (anchor && adParams && isAppStoreListing(anchor.href)) {
      pixelFired = trackCustom("AppStoreRedirect", { location: window.location.pathname }) || pixelFired;
    }

    const target = event.target instanceof Element ? event.target : null;
    const element = target?.closest("[data-conversion-event]") as ConversionElement | null;

    if (element?.dataset.conversionEvent) {
      const payload = {
        event_category: "conversion",
        event_label: element.dataset.conversionLabel || element.textContent?.trim() || "",
        location: element.dataset.conversionLocation || window.location.pathname,
        plan: element.dataset.conversionPlan || undefined,
        link_url:
          element instanceof HTMLAnchorElement
            ? element.href
            : element.getAttribute("href") || undefined,
        page_path: window.location.pathname,
        // The hero CTA copy test arm (composables/useCtaTest.ts), only on enrolled clicks.
        ...(isCtaVariant(element.dataset.conversionVariant)
          ? { cta_test: CTA_TEST_ID, cta_variant: element.dataset.conversionVariant }
          : {}),
      };

      const metaEvent = META_CLICK_EVENTS[element.dataset.conversionEvent];
      if (metaEvent) {
        pixelFired = trackCustom(metaEvent, { location: payload.location, plan: payload.plan }) || pixelFired;
      }

      if (typeof window.gtag === "function") {
        window.gtag("event", element.dataset.conversionEvent, payload);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: element.dataset.conversionEvent,
          ...payload,
        });
      }
    }

    if (pixelFired && anchor && leavesPage(event, anchor)) holdNavigation(event, anchor.href);
  };

  document.addEventListener("pointerdown", handlePointerDown, { capture: true, passive: true });
  document.addEventListener("click", handleClick, { capture: true });
});
